import { Save, X } from 'lucide-react';
import { useState, useRef, useEffect, useMemo, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { z } from 'zod';
import { useForm, type Resolver } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuthQuery } from '../../auth/api/auth.queries';
import { useUpdateUserMutation } from '@/features/user';
import { toast } from 'sonner';

const profileSchema = z.object({
  nrp: z.string().min(1, 'nrp is required'),
  fullname: z.string().min(1, 'Full name is required'),
  role: z.string().min(1, 'Role is required'),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

const profileResolver: Resolver<ProfileFormValues> = async (values) => {
  const result = profileSchema.safeParse(values);

  if (result.success) {
    return { values: result.data, errors: {} };
  }

  const errors = result.error.issues.reduce(
    (acc, issue) => {
      const field = issue.path?.[0];
      if (typeof field === 'string' && issue.message) {
        acc[field as keyof ProfileFormValues] = {
          type: issue.code,
          message: issue.message,
        };
      }
      return acc;
    },
    {} as Record<string, { type: string; message: string }>
  );

  return {
    values: {},
    errors,
  };
};

export function ProfileTabContent() {
  const { userProfile, isLoading } = useAuthQuery();
  const updateUserMutation = useUpdateUserMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ProfileFormValues>({
    resolver: profileResolver,
    defaultValues: {
      nrp: '',
      fullname: '',
      role: '',
    },
  });

  // Update form when user profile data is loaded
  useEffect(() => {
    if (userProfile) {
      reset({
        nrp: userProfile?.nrp,
        fullname: userProfile?.fullname || '',
        role: userProfile?.role,
      });
    }
  }, [userProfile, reset]);

  // Generate user initials from name
  const userInitials = useMemo(() => {
    if (!userProfile?.fullname) return 'U';
    const nameParts = userProfile.fullname.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return userProfile.fullname.substring(0, 2).toUpperCase();
  }, [userProfile]);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setImageError(null);

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setImageError('File must be an image (JPG, PNG, GIF)');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      // 2MB
      setImageError('File size must be less than 2MB');
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const onSubmit = async (values: ProfileFormValues) => {
    if (!userProfile?.userId) {
      toast.error('User ID not found. Please re-login and try again.');
      return;
    }

    try {
      await updateUserMutation.mutateAsync({
        id: userProfile.userId,
        data: {
          fullname: values.fullname,
        },
      });

      toast.success('Profile updated successfully');
    } catch (error: any) {
      console.error('[Profile] Update failed:', error);
      toast.error(error?.message || 'Failed to update profile');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-400">Loading profile...</div>
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Title */}
        <h3 className="text-lg font-semibold text-slate-50">User Profile</h3>

        {/* Avatar Section */}
        <div className="flex items-start gap-4">
          {/* Avatar Circle */}
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={() => setIsPreviewOpen(true)}
              className="group relative w-20 h-20 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-emerald-500/70 focus:ring-offset-2 focus:ring-offset-slate-950"
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Avatar preview"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              ) : (
                <div className="w-full h-full bg-teal-500 flex items-center justify-center transition-colors group-hover:bg-teal-600">
                  <span className="text-2xl font-semibold text-white">
                    {userInitials}
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 sticky group-hover:bg-black/10 transition-colors" />
            </button>
          </div>

          {/* Change Avatar Button & Info */}
          <div className="flex flex-col gap-2 justify-center items-start">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'inline-flex items-center justify-center rounded-lg border border-slate-700',
                'bg-slate-800/50 px-4 py-2 text-sm font-medium text-slate-200',
                'hover:bg-slate-800 hover:text-slate-50 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950'
              )}
            >
              Change Avatar
            </button>
            <div className="flex flex-col gap-1">
              <p className="text-xs text-slate-500">JPG, PNG or GIF, max 2MB</p>
              {imageError && (
                <p className="text-xs text-rose-400">{imageError}</p>
              )}
            </div>
          </div>
        </div>

        {/* Form Fields - Two Columns */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
          {/* nrp */}
          <div className="space-y-2">
            <Label>
              nrp <span className="text-white">*</span>
            </Label>
            <Input
              type="text"
              placeholder="nrp"
              {...register('nrp')}
              className={cn(
                'bg-slate-800/50 cursor-not-allowed',
                errors.nrp && 'border-red-500 focus-visible:ring-red-500'
              )}
              readOnly
            />
            {errors.nrp && (
              <p className="text-xs text-rose-400">{errors.nrp.message}</p>
            )}
          </div>

          {/* Full Name */}
          <div className="space-y-2">
            <Label>
              Full Name <span className="text-white">*</span>
            </Label>
            <Input
              type="text"
              placeholder="Enter full name"
              {...register('fullname')}
              className={cn(
                errors.fullname && 'border-red-500 focus-visible:ring-red-500'
              )}
            />
            {errors.fullname && (
              <p className="text-xs text-rose-400">{errors.fullname.message}</p>
            )}
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label>Role</Label>
            <Input
              type="text"
              placeholder="Operations Commander"
              {...register('role')}
              readOnly
              className={cn(
                'bg-slate-800/50 cursor-not-allowed',
                errors.role && 'border-red-500 focus-visible:ring-red-500'
              )}
            />
            {errors.role && (
              <p className="text-xs text-rose-400">{errors.role.message}</p>
            )}
          </div>
        </div>

        {/* Email */}
        {/* <div className="space-y-2">
            <Label>
              Email <span className="text-white">*</span>
            </Label>
            <Input
              type="email"
              placeholder="you@example.com"
              {...register('email')}
              className={cn(
                errors.email && 'border-red-500 focus-visible:ring-red-500'
              )}
            />
            {errors.email && (
              <p className="text-xs text-rose-400">{errors.email.message}</p>
            )}
          </div> */}

        {/* Department */}
        {/* <div className="space-y-2">
            <Label>
              Department <span className="text-white">*</span>
            </Label>
            <Input
              type="text"
              placeholder="Flight Operations"
              {...register('department')}
              className={cn(
                errors.department && 'border-red-500 focus-visible:ring-red-500'
              )}
            />
            {errors.department && (
              <p className="text-xs text-rose-400">
                {errors.department.message}
              </p>
            )}
          </div> */}

        {/* Bio Section */}
        {/* <div className="space-y-2">
          <Label htmlFor="bio" className="text-sm font-medium">
            Bio
          </Label>
          <Textarea
            placeholder="Tell us about your experience..."
            rows={4}
            {...register('bio')}
            className={cn(
              errors.bio && 'border-red-500 focus-visible:ring-red-500'
            )}
          />
          {errors.bio && (
            <p className="text-xs text-rose-400">{errors.bio.message}</p>
          )}
        </div> */}

        {/* Save Changes Button */}
        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            disabled={isSubmitting || updateUserMutation.isPending}
            className={cn(isSubmitting && 'opacity-80 cursor-not-allowed')}
          >
            <Save className="w-4 h-4" />
            <span>{isSubmitting ? 'Saving...' : 'Save Changes'}</span>
          </Button>
        </div>
      </form>
      {/* Image Preview Modal - Portal to body for full-screen overlay */}
      {isPreviewOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="relative max-w-2xl w-full bg-slate-900 rounded-xl overflow-hidden shadow-2xl ring-1 ring-slate-800">
              <button
                type="button"
                onClick={() => setIsPreviewOpen(false)}
                className="absolute top-4 right-4 p-2 rounded-full bg-slate-900/50 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors z-10"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center justify-center p-8 min-h-[300px] bg-[url('/grid-pattern.svg')]">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Avatar preview large"
                    className="max-h-[70vh] w-auto object-contain rounded-lg shadow-lg"
                  />
                ) : (
                  <div className="w-48 h-48 rounded-full bg-teal-500 flex items-center justify-center shadow-xl">
                    <span className="text-6xl font-semibold text-white">
                      {userInitials}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-4 bg-slate-900 border-t border-slate-800 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(false)}
                  className="px-4 py-2 bg-slate-800 text-slate-200 text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
            {/* Backdrop click to close */}
            <button
              type="button"
              className="absolute inset-0 -z-10 cursor-default"
              onClick={() => setIsPreviewOpen(false)}
              aria-label="Close modal"
            />
          </div>,
          document.body
        )}
    </>
  );
}
